import React from 'react';
import {
  X,
  CheckCircle2,
  Clock,
  AlertCircle,
  Pause,
  Play,
  RotateCcw,
  Ban,
  User,
  Calendar,
  FileText,
  FileSignature,
  Mail,
  CreditCard,
  FileSpreadsheet,
  Ticket,
  CheckSquare,
  Tag,
  GitCommit,
  FolderPlus,
  ShieldCheck,
  ArrowRight,
  RefreshCw,
} from 'lucide-react';
import { AutomationActionType, AutomationRun } from '../../../types/automation';
import { safeFetch } from '../../../lib/api';
import { safePiiText, sanitizeEncryptedPiiBlocks } from '../../../lib/piiDisplay';
import { notifyToast } from '../../../lib/toastStore';

interface AutomationRunDetailModalProps {
  run: AutomationRun | null;
  isOpen: boolean;
  onClose: () => void;
  onRefresh?: () => void;
}

const ACTION_ICONS: Record<
  AutomationActionType,
  React.ComponentType<{ className?: string }>
> = {
  generate_document: FileText,
  send_sign_request: FileSignature,
  send_email: Mail,
  create_event: Calendar,
  create_invoice: CreditCard,
  create_quote: FileSpreadsheet,
  create_support_ticket: Ticket,
  create_internal_task: CheckSquare,
  tag_application: Tag,
  change_workflow_stage: GitCommit,
  update_application_status: RefreshCw,
  add_to_batch: FolderPlus,
  update_batch: FolderPlus,
  update_admin: ShieldCheck,
};

export const AutomationRunDetailModal: React.FC<AutomationRunDetailModalProps> = ({
  run: propRun,
  isOpen,
  onClose,
  onRefresh,
}) => {
  // Per-step disable: which step's inline confirm is open, and whether
  // a disable request is in flight. Hooks live above the early return.
  const [confirmingStepId, setConfirmingStepId] = React.useState<string | null>(null);
  const [disablingStep, setDisablingStep] = React.useState(false);

  // Slice 8: the modal no longer renders a stale open-time snapshot.
  // It refetches the run on open, after every action, and on a 5s
  // tick while open; rendering uses the freshest copy.
  const [liveRun, setLiveRun] = React.useState<AutomationRun | null>(null);
  // Stored run records can carry encrypted PII envelopes in applicant
  // fields and step payloads (legacy runs store applicantEmail as an
  // envelope map); sanitize the record before render so an envelope
  // can never be rendered as a React child (React error #31). The
  // sanitizer returns the same reference when no envelope is present.
  const freshestRun = liveRun && propRun && liveRun.id === propRun.id ? liveRun : propRun;
  const run = freshestRun ? sanitizeEncryptedPiiBlocks(freshestRun) : freshestRun;

  const refreshRun = React.useCallback(async () => {
    if (!propRun) return;
    try {
      const data = await safeFetch(`/api/admin/automations/runs/${propRun.id}`);
      if (data.run) setLiveRun(sanitizeEncryptedPiiBlocks(data.run));
    } catch {
      // Keep showing the last good copy; the next tick retries.
    }
  }, [propRun?.id]);

  React.useEffect(() => {
    if (!isOpen || !propRun) return;
    setLiveRun(null);
    refreshRun();
    const t = setInterval(refreshRun, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, propRun?.id]);

  if (!isOpen || !run) return null;

  const handleAction = async (action: 'pause' | 'resume' | 'cancel' | 'retry') => {
    try {
      await safeFetch(`/api/admin/automations/runs/${run.id}/${action}`, {
        method: 'POST',
      });
      await refreshRun();
      if (onRefresh) onRefresh();
    } catch (err: any) {
      notifyToast('error', `Failed to ${action} run: ${err.message || 'Unknown error'}`);
    }
  };

  const handleDisableStep = async (stepId: string) => {
    setDisablingStep(true);
    try {
      await safeFetch(`/api/admin/automations/runs/${run.id}/steps/${stepId}/disable`, {
        method: 'POST',
      });
      setConfirmingStepId(null);
      await refreshRun();
      if (onRefresh) onRefresh();
    } catch (err: any) {
      notifyToast('error', `Failed to disable step: ${err.message || 'Unknown error'}`);
    } finally {
      setDisablingStep(false);
    }
  };

  // Slice 7: progress + current-step derivation for the timeline.
  const completedStepCount = run.steps.filter((s) => s.status === 'completed').length;
  const runIsActive = run.status === 'pending' || run.status === 'running' || run.status === 'paused';

  const statusBadgeClass =
    run.status === 'completed'
      ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
      : run.status === 'running' || run.status === 'pending'
      ? 'bg-blue-50 text-blue-700 border-blue-200'
      : run.status === 'paused'
      ? 'bg-amber-50 text-amber-700 border-amber-200'
      : 'bg-rose-50 text-rose-700 border-rose-200';

  return (
    <div className="fixed inset-0 z-[9500] flex items-center justify-center bg-slate-950/70 backdrop-blur-xs p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden animate-scale-in text-slate-900">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-white shrink-0">
          <div className="flex items-center gap-3">
            <span className="font-mono text-xs font-black text-indigo-700 px-2.5 py-1 rounded-lg bg-indigo-50 border border-indigo-200">
              {run.runId}
            </span>
            <div>
              <h3 className="text-base font-extrabold text-slate-900">{run.automationName}</h3>
              <p className="text-xs text-slate-500">
                Assigned to: <strong className="text-slate-700">{safePiiText(run.applicantName)}</strong> (
                {run.applicationCustomId})
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span
              className={`px-3 py-1 rounded-full text-xs font-extrabold uppercase border ${statusBadgeClass}`}
            >
              {run.status.replace(/_/g, ' ')}
            </span>
            <button aria-label="Close" title="Close"
              onClick={onClose}
              className="p-2 text-slate-400 hover:text-slate-900 rounded-lg hover:bg-slate-100 transition-colors"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Content Body */}
        <div className="p-6 overflow-y-auto space-y-6 flex-1">
          {/* Overview Metadata Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
            <div className="p-3 rounded-xl bg-slate-50 border border-slate-200">
              <span className="text-slate-500 block mb-1">Version</span>
              <strong className="text-slate-900 font-extrabold">v{run.automationVersion}</strong>
            </div>
            <div className="p-3 rounded-xl bg-slate-50 border border-slate-200">
              <span className="text-slate-500 block mb-1">Initiated By</span>
              <strong className="text-slate-900 font-extrabold truncate block">
                {run.initiatedByName}
              </strong>
            </div>
            <div className="p-3 rounded-xl bg-slate-50 border border-slate-200">
              <span className="text-slate-500 block mb-1">Started At</span>
              <strong className="text-slate-900 font-extrabold">
                {new Date(run.createdAt).toLocaleDateString()}{' '}
                {new Date(run.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </strong>
            </div>
            <div className="p-3 rounded-xl bg-slate-50 border border-slate-200">
              <span className="text-slate-500 block mb-1">Next Action</span>
              <strong className="text-slate-900 font-extrabold">
                {run.nextScheduledStepAt
                  ? new Date(run.nextScheduledStepAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                  : 'None'}
              </strong>
            </div>
          </div>

          {/* Error Banner if failed */}
          {run.errorSummary && (
            <div className="p-4 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-xs flex items-start gap-3">
              <AlertCircle size={16} className="shrink-0 mt-0.5" />
              <div>
                <strong className="font-extrabold block">Execution Error</strong>
                <span>{safePiiText(run.errorSummary)}</span>
              </div>
            </div>
          )}

          {/* Slice 7: progress across the run's steps */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <h4 className="text-xs font-extrabold text-slate-700 uppercase tracking-wider">
                Progress
              </h4>
              <span className="text-[11px] font-bold text-slate-500">
                {completedStepCount} of {run.steps.length} steps completed
              </span>
            </div>
            <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
              <div
                className="h-full bg-indigo-500 rounded-full transition-all"
                style={{
                  width: `${run.steps.length ? Math.round((completedStepCount / run.steps.length) * 100) : 0}%`,
                }}
              />
            </div>
          </div>

          {/* Execution Steps Timeline */}
          <div>
            <h4 className="text-xs font-extrabold text-slate-700 uppercase tracking-wider mb-4">
              Execution Timeline ({run.steps.length} Steps)
            </h4>

            <div className="space-y-3">
              {run.steps.map((step, idx) => {
                const Icon = ACTION_ICONS[step.actionType] || FileText;
                const isCurrent = idx === run.currentStepIndex;

                let stepColor = 'border-slate-200 bg-slate-50 text-slate-500';
                if (step.status === 'completed') {
                  stepColor = 'border-emerald-200 bg-emerald-50 text-emerald-700';
                } else if (step.status === 'failed') {
                  stepColor = 'border-rose-200 bg-rose-50 text-rose-700';
                } else if (step.status === 'skipped' && step.disabledAt) {
                  stepColor = 'border-violet-200 bg-violet-50 text-violet-700';
                } else if (step.status === 'running' || step.status === 'scheduled') {
                  stepColor = 'border-blue-200 bg-blue-50 text-blue-700';
                }

                return (
                  <div
                    key={step.stepId}
                    className={`p-4 rounded-xl border transition-all ${stepColor} ${
                      isCurrent && runIsActive ? 'ring-2 ring-blue-300' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between gap-3 mb-2">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-white text-slate-500 border border-slate-200">
                          <Icon className="h-4 w-4" />
                        </div>
                        <div>
                          <span className="text-[10px] font-black text-slate-500 uppercase block">
                            Step {idx + 1}
                            {isCurrent && runIsActive && (
                              <span className="ml-2 px-1.5 py-0.5 rounded-full bg-blue-600 text-white">
                                Current step
                              </span>
                            )}
                          </span>
                          <strong className="text-xs font-extrabold text-slate-900">
                            {safePiiText(step.actionLabel)}
                          </strong>
                        </div>
                      </div>

                      <span
                        className={`text-[10px] font-black uppercase px-2.5 py-0.5 rounded-full border ${
                          step.status === 'completed'
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                            : step.status === 'failed'
                            ? 'bg-rose-50 text-rose-700 border-rose-200'
                            : step.status === 'skipped' && step.disabledAt
                            ? 'bg-violet-50 text-violet-700 border-violet-200'
                            : 'bg-blue-50 text-blue-700 border-blue-200'
                        }`}
                      >
                        {step.status === 'skipped' && step.disabledAt ? 'disabled' : step.status}
                      </span>
                    </div>

                    {/* Step details */}
                    <div className="text-[11px] text-slate-500 space-y-1 pl-11">
                      {step.scheduledAt && (
                        <p className="flex items-center gap-1.5 text-amber-700">
                          <Clock size={12} />
                          Scheduled for:{' '}
                          {new Date(step.scheduledAt).toLocaleString([], {
                            dateStyle: 'short',
                            timeStyle: 'short',
                          })}
                        </p>
                      )}
                      {step.executedAt && (
                        <p>
                          Executed at:{' '}
                          {new Date(step.executedAt).toLocaleString([], {
                            dateStyle: 'short',
                            timeStyle: 'short',
                          })}
                        </p>
                      )}
                      {step.completedAt && (
                        <p>
                          Completed at:{' '}
                          {new Date(step.completedAt).toLocaleString([], {
                            dateStyle: 'short',
                            timeStyle: 'short',
                          })}
                        </p>
                      )}
                      {step.disabledAt && (
                        <p className="text-violet-700">
                          Disabled by {step.disabledByName || 'an admin'} ·{' '}
                          {new Date(step.disabledAt).toLocaleString([], {
                            dateStyle: 'short',
                            timeStyle: 'short',
                          })}
                          {step.disabledReason ? ` — ${step.disabledReason}` : ''}
                        </p>
                      )}
                      {step.error && (
                        <p className="text-rose-700 font-mono bg-rose-50 p-2 rounded-lg border border-rose-200 mt-1">
                          {safePiiText(step.error.message)}
                        </p>
                      )}
                      {step.attempts > 0 && (
                        <p>
                          Attempts: {step.attempts} of {step.maxAttempts}
                        </p>
                      )}
                      {step.attemptHistory && step.attemptHistory.length > 0 && (
                        <div className="mt-1 space-y-1">
                          <p className="font-bold text-slate-600">Attempt history</p>
                          {step.attemptHistory.map((entry, i) => (
                            <p key={i} className="text-rose-700">
                              Attempt {entry.attempt} ·{' '}
                              {new Date(entry.at).toLocaleString([], {
                                dateStyle: 'short',
                                timeStyle: 'short',
                              })}{' '}
                              — {safePiiText(entry.error)}
                            </p>
                          ))}
                        </div>
                      )}
                      {step.output && (
                        <div className="mt-2 p-2 rounded-lg bg-slate-50 font-mono text-[10px] text-slate-700 overflow-x-auto border border-slate-200">
                          <pre>{JSON.stringify(sanitizeEncryptedPiiBlocks(step.output), null, 2)}</pre>
                        </div>
                      )}
                      {(step.status === 'pending' || step.status === 'scheduled') &&
                        (run.status === 'pending' ||
                          run.status === 'running' ||
                          run.status === 'paused') &&
                        (confirmingStepId === step.stepId ? (
                          <div className="mt-2 p-2.5 rounded-lg bg-violet-50 border border-violet-200">
                            <p className="text-violet-700 font-semibold">
                              Disable this step? It will be skipped when its turn comes and
                              will not run.
                            </p>
                            <div className="flex items-center gap-2 mt-2">
                              <button
                                onClick={() => handleDisableStep(step.stepId)}
                                disabled={disablingStep}
                                className="px-2.5 py-1 rounded-lg bg-violet-100 hover:bg-violet-200 border border-violet-300 text-violet-800 text-[11px] font-bold transition-all disabled:opacity-50"
                              >
                                {disablingStep ? 'Disabling…' : 'Disable step'}
                              </button>
                              <button
                                onClick={() => setConfirmingStepId(null)}
                                disabled={disablingStep}
                                className="px-2.5 py-1 rounded-lg text-slate-500 hover:text-slate-900 hover:bg-slate-100 text-[11px] font-bold transition-all disabled:opacity-50"
                              >
                                Keep step
                              </button>
                            </div>
                          </div>
                        ) : (
                          <button
                            onClick={() => setConfirmingStepId(step.stepId)}
                            className="mt-1.5 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-violet-50 hover:bg-violet-100 border border-violet-200 text-violet-700 text-[11px] font-bold transition-all"
                          >
                            <Ban size={11} />
                            Disable step
                          </button>
                        ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between bg-slate-50 shrink-0">
          <div className="flex items-center gap-2">
            {run.status === 'running' && (
              <button
                onClick={() => handleAction('pause')}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-amber-50 hover:bg-amber-100 border border-amber-200 text-amber-700 text-xs font-bold transition-all"
              >
                <Pause size={13} />
                Pause Run
              </button>
            )}
            {run.status === 'paused' && (
              <button
                onClick={() => handleAction('resume')}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-blue-50 hover:bg-blue-100 border border-blue-200 text-blue-700 text-xs font-bold transition-all"
              >
                <Play size={13} />
                Resume Run
              </button>
            )}
            {run.status === 'failed' && (
              <button
                onClick={() => handleAction('retry')}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 text-indigo-700 text-xs font-bold transition-all"
              >
                <RotateCcw size={13} />
                Retry Failed Steps
              </button>
            )}
            {run.status !== 'completed' && run.status !== 'cancelled' && (
              <button
                onClick={() => handleAction('cancel')}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-rose-50 hover:bg-rose-100 border border-rose-200 text-rose-700 text-xs font-bold transition-all"
              >
                <Ban size={13} />
                Cancel Run
              </button>
            )}
          </div>

          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl text-xs font-bold text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
